import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError, fileUrl, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUSES, STATUS_MAP, PRIORITIES, PRIORITY_MAP, statusChipClass, priorityChipClass } from "@/lib/labels";
import ThreatMeter from "@/components/ThreatMeter";
import { ChevronLeft, Terminal, ShieldAlert, Wrench, Lock, FileDown, Trash2 } from "lucide-react";
import Comments from "@/components/Comments";

export default function BugDetail() {
  const { id } = useParams();
const delBug = async () => {
    if (!window.confirm("¿Confirmas eliminar este ticket de forma permanente?")) return;
    try {
      await api.delete(`/bugs/${id}`);
      window.location.href = "/dashboard";
    } catch (e) {
      setErr(formatApiError(e));
    }
  };
  const { user } = useAuth();
  const [bug, setBug] = useState(null);
  const [err, setErr] = useState("");
  const [updating, setUpdating] = useState(false);
  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "admin" || user?.role === "agent";

  const load = () => {
    api.get(`/bugs/${id}`).then((r) => setBug(r.data)).catch((e) => setErr(formatApiError(e)));
  };
  useEffect(load, [id]);

  const changeStatus = async (s) => {
    setUpdating(true); setErr("");
    try { await api.patch(`/bugs/${id}/status`, { status: s }); load(); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setUpdating(false); }
  };

  const changePriority = async (p) => {
    setUpdating(true); setErr("");
    try { await api.patch(`/bugs/${id}/priority`, { priority: p }); load(); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setUpdating(false); }
  };

  if (err) return <div className="p-8 text-red-500 shake" data-testid="detail-error">{err}</div>;
  if (!bug) return <div className="p-8 text-red-500 blink-cursor" data-testid="detail-loading">Cargando</div>;

  const Row = ({ label, value, tid, mono = false }) => (
    <div>
      <div className="term-label">{label}</div>
      <div className={`text-neutral-100 whitespace-pre-wrap ${mono ? "font-mono text-sm" : "text-base"}`} data-testid={tid}>{value}</div>
    </div>
  );

  const statusShort = STATUS_MAP[bug.status]?.short || bug.status;

  return (
    <div className="max-w-4xl mx-auto p-6 md:p-8" data-testid="bug-detail-page">
<div className="flex items-center justify-between mb-6">
  {/* Botón Volver a la izquierda */}
  <Link to="/dashboard" data-testid="detail-back-btn" className="term-btn-ghost">
    <ChevronLeft size={14} /> Volver
  </Link>

  {/* Botones de acción a la derecha */}
  <div className="flex items-center gap-2">
    <a
      href={`${API}/bugs/${bug.id}/pdf?auth=${encodeURIComponent(localStorage.getItem("bt_token") || "")}`}
      target="_blank"
      rel="noopener noreferrer"
      className="term-btn flex items-center gap-1.5"
    >
      <FileDown size={14} /> Exportar PDF
    </a>

    {isAdmin && (
  <button
    onClick={delBug}
    className="term-btn text-red-500 border-red-500/30 hover:bg-red-500/10 flex items-center gap-1.5"
    title="Eliminar Ticket"
  >
    <Trash2 size={14} /> Eliminar
  </button>
)}
  </div>
</div>

      <div className="flex items-center gap-2 text-red-500 text-xs uppercase tracking-[0.3em] mb-2">
        <Terminal size={14} /> // bug_id: {bug?.id ? bug.id.slice(0,8) : ""}
      </div>
      <h1 className="text-3xl md:text-4xl uppercase tracking-tighter font-bold mb-2" data-testid="detail-title">{bug.title}</h1>
      <div className="flex flex-wrap gap-2 items-center mb-6">
        <span className={`chip chip-${bug.severity}`} data-testid="detail-severity-chip">{bug.severity}</span>
        <span className={statusChipClass(bug.status)} data-testid="detail-status-chip">{statusShort}</span>
        <ThreatMeter value={bug.priority} testid="detail-priority-meter" />
        <span className="text-xs text-neutral-500 uppercase tracking-[0.2em]">
          {bug?.created_at ? new Date(bug.created_at).toLocaleString() : ""}
        </span>
      </div>

      <div className="term-panel p-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Row label="Severidad" value={bug.severity} tid="detail-severity" />
          <Row
            label="Fallo (Origen)"
            value={bug.browser === "Otro" && bug.browser_other ? `Otro: ${bug.browser_other}` : bug.browser}
            tid="detail-browser"
          />
          <Row label="Sistema" value={bug.os} tid="detail-os" />
          <Row label="Estado" value={statusShort} tid="detail-status" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="term-label flex items-center gap-2">
              <ShieldAlert size={12} /> Nivel de amenaza (Prioridad)
            </div>
            <div className="text-neutral-100 text-base flex items-center gap-3 mt-1" data-testid="detail-priority-info">
              <ThreatMeter value={bug.priority} />
              {bug.priority_overridden ? (
                <span className="text-[0.65rem] uppercase tracking-[0.2em] text-amber-400">
                  · Ajustada manualmente (sugerida: {(PRIORITY_MAP[bug.priority_suggested]?.label || "?")})
                </span>
              ) : (
                <span className="text-[0.65rem] uppercase tracking-[0.2em] text-neutral-500">
                  · Sugerida automáticamente
                </span>
              )}
            </div>
          </div>
          <div>
            <div className="term-label">Reportero</div>
            <div className="text-neutral-100 text-sm">{bug.reporter_name} · {bug.reporter_email}</div>
          </div>
        </div>

        <hr className="border-red-500/20" />
        <Row label="Pasos para reproducir" value={bug.steps} tid="detail-steps" mono />
        <Row label="Resultado esperado" value={bug.expected} tid="detail-expected" />
        <Row label="Resultado actual" value={bug.actual} tid="detail-actual" />

        {bug.evidence_text && <Row label="Evidencia (texto)" value={bug.evidence_text} tid="detail-evidence-text" mono />}

        {bug.evidence_file_id && (
          <div>
            <div className="term-label">Evidencia (imagen)</div>
            <img
              data-testid="detail-evidence-image"
              src={fileUrl(bug.evidence_file_id)}
              alt="evidencia"
              className="max-w-full border border-red-500/40"
            />
          </div>
        )}
      </div>

      {isManager ? (
        <div className="term-panel p-4 mt-6 space-y-4" data-testid="admin-controls">
          <div className="flex items-center gap-2 text-red-500 text-xs uppercase tracking-[0.3em]">
            <Wrench size={12} /> {isAdmin ? "Panel Admin" : "Panel Agente"}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs uppercase tracking-[0.2em] text-neutral-500 mr-2">Estado:</span>
            {STATUSES.map((s) => (
              <button
                key={s.value}
                data-testid={`status-btn-${s.value}`}
                disabled={updating || bug.status === s.value}
                onClick={() => changeStatus(s.value)}
                className={bug.status === s.value ? "term-btn" : "term-btn-ghost"}
              >
                {s.short}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs uppercase tracking-[0.2em] text-neutral-500 mr-2">Amenaza:</span>
            {PRIORITIES.map((p) => (
              <button
                key={p.value}
                data-testid={`priority-btn-${p.value}`}
                disabled={updating || bug.priority === p.value}
                onClick={() => changePriority(p.value)}
                className={bug.priority === p.value ? "term-btn" : "term-btn-ghost"}
              >
                {p.label}
              </button>
            ))}
            {bug.priority_overridden && (
              <button
                data-testid="priority-reset-btn"
                disabled={updating}
                onClick={() => changePriority(bug.priority_suggested)}
                className="term-btn-ghost"
                title="Restaurar a la prioridad sugerida por el sistema"
              >
                Restaurar sugerida ({(PRIORITY_MAP[bug.priority_suggested]?.label || "?")})
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-6 term-panel p-3 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-neutral-500" data-testid="viewer-notice">
          <Lock size={12} /> Solo lectura. Los administradores gestionan estado y nivel de amenaza.
        </div>
      )}

      <Comments bugId={bug.id} />
    </div>
  );
}
